import { DEFAULT_VIEW_OPTIONS, findLabel, TAGS_FILTER_TYPES_OPTIONS } from "./config";
import { DEFAULT_CATEGORY_CONFIG } from "./initState";
import CATEGORY_KEYS, { CATEGORY_CONFIG_KEYS } from "./keys";
import { trimTextTranslations } from "utils/objectUtils";

const getView = (config) => {
  if(!config || !config[CATEGORY_CONFIG_KEYS.VIEW]) return ({label: findLabel(DEFAULT_VIEW_OPTIONS, 'gallery'), value: 'gallery'});
  const view = config[CATEGORY_CONFIG_KEYS.VIEW];

  return {label: findLabel(DEFAULT_VIEW_OPTIONS, view), value: view};
}

const getTagFilter = (config) => {
  if(!config || !config[CATEGORY_CONFIG_KEYS.FILTER_TYPE]) return ({label: findLabel(TAGS_FILTER_TYPES_OPTIONS, 'accumulative'), value: 'accumulative'});
  const tags = config[CATEGORY_CONFIG_KEYS.FILTER_TYPE];

  return {label: findLabel(TAGS_FILTER_TYPES_OPTIONS, tags), value: tags};
}

const getAvailability = (config) => {
  let availabilityProps  ={
    alwaysAvailable: true,
    schedule: new Map()
  };
  
  if(!config[CATEGORY_CONFIG_KEYS.AVAILABILITY]) return availabilityProps;
  
  const availability = config[CATEGORY_CONFIG_KEYS.AVAILABILITY];
  const {alwaysAvailable = true, schedule = null, inactives = null} = availability;

  if( !alwaysAvailable || !!schedule || !!inactives ){
    availabilityProps.alwaysAvailable = alwaysAvailable;
    availabilityProps.schedule = getRulesByTimeRange(schedule, inactives);
  }

  return availabilityProps;
}

const cleanAvailability = (availability) => {
  let configAvailability = {
    alwaysAvailable: availability.alwaysAvailable,
    schedule: null,
    inactives: null,
  };

  if(!availability.alwaysAvailable || availability.schedule.size > 0){
    const { schedule, inactives } = getRulesBySharedDays(availability.schedule);
    configAvailability.schedule = schedule;
    configAvailability.inactives = inactives;
  }
  
  return configAvailability;
}

export const toFormData = (category) => {

  const processed = {
    ...category,
    [CATEGORY_KEYS.TRANSLATIONS]: trimTextTranslations(category[CATEGORY_KEYS.TRANSLATIONS]),
    [CATEGORY_KEYS.CONFIG]: {
      ...DEFAULT_CATEGORY_CONFIG(),
      ...(category.config ?? {}),
      [CATEGORY_CONFIG_KEYS.MESSAGE]: {
        ...category.config[CATEGORY_CONFIG_KEYS.MESSAGE], 
        [CATEGORY_CONFIG_KEYS.MESSAGE_ACTIVE]: category.config[CATEGORY_CONFIG_KEYS.MESSAGE][CATEGORY_CONFIG_KEYS.MESSAGE_ACTIVE] ?? true
      },
      [CATEGORY_CONFIG_KEYS.VIEW]: getView(category.config),
      [CATEGORY_CONFIG_KEYS.FILTER_TYPE]: getTagFilter(category.config),
      [CATEGORY_CONFIG_KEYS.AVAILABILITY]: getAvailability(category.config),
    }
  };
  return processed;
};

export const toSubmitObject = (formData) => {

  const item = {
    [CATEGORY_KEYS.ACTIVE]: formData[CATEGORY_KEYS.ACTIVE],
    [CATEGORY_KEYS.TRANSLATIONS]: trimTextTranslations(formData[CATEGORY_KEYS.TRANSLATIONS]),
    [CATEGORY_KEYS.CONFIG]: {
      ...formData.config,
      [CATEGORY_CONFIG_KEYS.VIEW]: formData.config[CATEGORY_CONFIG_KEYS.VIEW].value,
      [CATEGORY_CONFIG_KEYS.FILTER_TYPE]: formData.config[CATEGORY_CONFIG_KEYS.FILTER_TYPE].value,
      [CATEGORY_CONFIG_KEYS.AVAILABILITY]: cleanAvailability(formData.config[CATEGORY_CONFIG_KEYS.AVAILABILITY]),
    }
  };

  const image = formData[CATEGORY_KEYS.IMAGE];
  item[CATEGORY_KEYS.IMAGE] = image?.file ? image.file : image || null;

  return item;
};

function getRulesBySharedDays(rules) {
  const customRulesGroup = new Map();
  const inactives = new Set();
  const allDayRules = [];

  const rulesArray = Array.from(rules.values());
  
  const daysMap = new Map();
  for (const { timeRange, allDayAvailable, days, isActive } of rulesArray) {
    if(!days) continue;

    if(!isActive){
      const inactive = JSON.stringify({timeRange, allDayAvailable, days, isActive});
      inactives.add(inactive);
      continue;
    }

    if(allDayAvailable){
      allDayRules.push({timeRange, allDayAvailable, days, isActive});
      continue;
    }

    //block of days, each one with own schedule
    for (const day of days) {
      if (!daysMap.has(day)) {
        daysMap.set(day, []);
      }
      daysMap.get(day).push({ start: timeRange.start, end: timeRange.end });
    }
  }

  // match days with common schedule
  for (const [day, ranges] of daysMap.entries()) {
    const key = JSON.stringify(ranges);
    if (!customRulesGroup.has(key)) {
      customRulesGroup.set(key, { days: [], timeRanges: ranges });
    }
    customRulesGroup.get(key).days.push(day);
  }
  
  let schedule = {};
  if(allDayRules.length > 0)
    schedule['allDayAvailable'] = [...allDayRules];
  if(customRulesGroup.size > 0)
    schedule['custom'] = [...customRulesGroup.values()];
  
  return {
    schedule,
    inactives:inactives.size > 0 ?  Array.from(inactives) : null,
  };
}

const getRulesByTimeRange = ( schedule, inactives = [] ) => {
  const rules = new Map();

  if(!!schedule && Object.keys(schedule).length > 0){

    if(schedule.allDayAvailable){
      for(const rule of schedule.allDayAvailable){
        const {
          days,
          isActive = true,
          allDayAvailable = true,
          timeRange = {start: null, end: null},
        } = rule;

        rules.set(
          new Date(), {
          days,
          timeRange,
          isActive,
          allDayAvailable,
        });
      }
    }
    if(schedule.custom){
      for( const { days, timeRanges } of schedule.custom ){
        timeRanges.forEach(({ start, end }) => {
          const key = `${start}-${end}`;
          //Each item use range as a key and need to create the array of days
          if (!rules.has(key)) {
            rules.set(key, {
              timeRange: { start, end },
              days: [...days],
              isActive: true
            });
          } else {
            const existing = rules.get(key);
            existing.days.push(...days);
            existing.days = [...new Set(existing.days)];
          }
        });
      };
    }
    
  }

  if(!!inactives && inactives.length > 0){
    for(const inactiveString of inactives){
      const inactive = JSON.parse(inactiveString);
      const key = `${inactive.timeRange.start}-${inactive.timeRange.end}`;
      rules.set(key, inactive);
    }
  }
  
  return rules;
}